import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  TablePagination,
  TableSortLabel,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as ActivateIcon,
  Pause as DeactivateIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import CountdownTimer from "../components/CountdownTimer";

const OffersManagement = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalOffers, setTotalOffers] = useState(0);
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [confirmationDialog, setConfirmationDialog] = useState({ 
    open: false, 
    title: "", 
    message: "", 
    offerToActivate: null,
    activeOfferName: ""
  });

  const [formData, setFormData] = useState({
    name: "",
    expiryDate: "",
    totalSeats: "",
    remainingSeats: "",
    discountPercentage: "",
    isActive: false,
  });

  const REACT_APP_SUPERADMIN_APP_API_BASE_URL = process.env.REACT_APP_SUPERADMIN_APP_API_BASE_URL || "http://localhost:4000";

  const handleSidebarStateChange = (isOpen) => {
    setSidebarOpen(isOpen);
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: "", severity: "success" });
  };

  const handleConfirmActivation = async () => {
    if (confirmationDialog.offerToActivate) {
      await activateOfferDirectly(confirmationDialog.offerToActivate);
    }
    setConfirmationDialog({ open: false, title: "", message: "", offerToActivate: null, activeOfferName: "" });
  };

  const handleCancelActivation = () => {
    setConfirmationDialog({ open: false, title: "", message: "", offerToActivate: null, activeOfferName: "" });
  };

  const fetchOffers = async () => {
    setLoading(true);
    try {
      // Add cache-busting parameter to prevent browser caching
      const cacheBuster = `_t=${Date.now()}`;
      const response = await fetch(
        `${REACT_APP_SUPERADMIN_APP_API_BASE_URL}/offers?page=${page + 1}&limit=${rowsPerPage}&sortBy=${sortBy}&sortOrder=${sortOrder}&${cacheBuster}`,
        {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0',
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        setOffers(data.data.offers);
        setTotalOffers(data.data.total);
      } else {
        showSnackbar(data.message, "error");
      }
    } catch (error) {
      showSnackbar("Failed to fetch offers", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
  }, [page, rowsPerPage, sortBy, sortOrder]);

  const handleCreateOffer = () => {
    setEditingOffer(null);
    setFormData({
      name: "",
      expiryDate: "",
      totalSeats: "",
      remainingSeats: "",
      discountPercentage: "",
      isActive: false,
    });
    setOpenDialog(true);
  };

  const handleEditOffer = (offer) => {
    setEditingOffer(offer);
    setFormData({
      name: offer.name,
      expiryDate: new Date(offer.expiryDate).toISOString().slice(0, 16),
      totalSeats: offer.totalSeats.toString(),
      remainingSeats: offer.remainingSeats.toString(),
      discountPercentage: offer.discountPercentage.toString(),
      isActive: offer.isActive,
    });
    setOpenDialog(true);
  };

  const handleSaveOffer = async () => {
    try {
      const offerData = {
        ...formData,
        totalSeats: parseInt(formData.totalSeats),
        remainingSeats: parseInt(formData.remainingSeats),
        discountPercentage: parseInt(formData.discountPercentage),
        expiryDate: new Date(formData.expiryDate).toISOString(),
      };

      const url = editingOffer
        ? `${REACT_APP_SUPERADMIN_APP_API_BASE_URL}/offers/${editingOffer._id}`
        : `${REACT_APP_SUPERADMIN_APP_API_BASE_URL}/offers`;
      const method = editingOffer ? "PATCH" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(offerData),
      });

      const data = await response.json();
      if (data.success) {
        showSnackbar(
          editingOffer ? "Offer updated successfully" : "Offer created successfully"
        );
        setOpenDialog(false);
        fetchOffers();
      } else {
        showSnackbar(data.message, "error");
      }
    } catch (error) {
      showSnackbar("Failed to save offer", "error");
    }
  };

  const handleDeleteOffer = async (offerId) => {
    if (window.confirm("Are you sure you want to delete this offer?")) {
      try {
        const response = await fetch(`${REACT_APP_SUPERADMIN_APP_API_BASE_URL}/offers/${offerId}`, {
          method: "DELETE",
        });
        const data = await response.json();
        if (data.success) {
          showSnackbar("Offer deleted successfully");
          fetchOffers();
        } else {
          showSnackbar(data.message, "error");
        }
      } catch (error) {
        showSnackbar("Failed to delete offer", "error");
      }
    }
  };

  const handleActivateOffer = async (offerId) => {
    // Check if there's already an active offer
    const activeOffer = offers.find(offer => offer.isActive);
    
    if (activeOffer && activeOffer._id !== offerId) {
      // Show confirmation dialog
      const offerToActivate = offers.find(offer => offer._id === offerId);
      setConfirmationDialog({
        open: true,
        title: "Active Offer Confirmation",
        message: `The offer "${activeOffer.name}" is currently active. Do you want to deactivate it and activate "${offerToActivate.name}" instead?`,
        offerToActivate: offerId,
        activeOfferName: activeOffer.name
      });
    } else {
      // No active offer, proceed with activation
      await activateOfferDirectly(offerId);
    }
  };

  const activateOfferDirectly = async (offerId) => {
    try {
      const response = await fetch(`${REACT_APP_SUPERADMIN_APP_API_BASE_URL}/offers/${offerId}/activate`, {
        method: "PATCH",
      });
      const data = await response.json();
      if (data.success) {
        showSnackbar("Offer activated successfully");
        fetchOffers();
      } else {
        showSnackbar(data.message, "error");
      }
    } catch (error) {
      showSnackbar("Failed to activate offer", "error");
    }
  };

  const handleDeactivateOffer = async (offerId) => {
    try {
      const response = await fetch(`${REACT_APP_SUPERADMIN_APP_API_BASE_URL}/offers/${offerId}/deactivate`, {
        method: "PATCH",
      });
      const data = await response.json();
      if (data.success) {
        showSnackbar("Offer deactivated successfully");
        fetchOffers();
      } else {
        showSnackbar(data.message, "error");
      }
    } catch (error) {
      showSnackbar("Failed to deactivate offer", "error");
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (property) => {
    const isAsc = sortBy === property && sortOrder === "asc";
    setSortOrder(isAsc ? "desc" : "asc");
    setSortBy(property);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div>
      <Sidebar onSidebarStateChange={handleSidebarStateChange} />
      <Navbar sidebarOpen={sidebarOpen} />
      
      <Box 
        sx={{ 
          ml: sidebarOpen ? "220px" : "100px", 
          p: { xs: 2, sm: 3, md: 4, xl: 4 },
          backgroundColor: "#fafafa",
          minHeight: "100vh",
          transition: "margin-left 0.3s ease",
          fontFamily: "Outfit"
        }}
      >
        {/* Header Section */}
        <Box sx={{ marginBottom: "32px" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <h1
              style={{
                fontSize: "18px",
                fontWeight: 700,
                color: "#2B3674",
                textAlign: "left",
                margin: "0 0 8px 0",
                lineHeight: "1.4",
                fontFamily: "Outfit",
              }}
            >
              Offers Management
            </h1>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateOffer}
              sx={{ 
                bgcolor: "#9048FB", 
                "&:hover": { bgcolor: "#7c3aed" },
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                px: 3,
                py: 1.5,
                fontFamily: "Outfit"
              }}
            >
              Create New Offer
            </Button>
          </Box>
          <p
            style={{
              color: "#A3AED0",
              fontSize: "14px",
              margin: 0,
              textAlign: "left",
              fontWeight: 400,
              fontFamily: "Outfit",
            }}
          >
            Manage and monitor all promotional offers and discounts
          </p>
        </Box>

        {/* Main Content Card */}
        <Box
          sx={{
            backgroundColor: "#ffffff",
            borderRadius: "16px",
            padding: { xs: "16px", sm: "24px", md: "32px", xl: "40px" },
            boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
            border: "1px solid #f1f5f9",
          }}
        >
          <TableContainer 
            component={Paper}
            sx={{
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
              boxShadow: "none",
              "& .MuiTable-root": {
                borderCollapse: "separate",
                borderSpacing: 0,
              }
            }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f8fafc",
                    "& .MuiTableCell-head": {
                      backgroundColor: "#f8fafc",
                      borderBottom: "1px solid #e5e7eb",
                      fontWeight: 600,
                      fontSize: "14px",
                      color: "#374151",
                      fontFamily: "Outfit",
                      py: 2,
                    }
                  }}
                >
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === "name"}
                      direction={sortBy === "name" ? sortOrder : "asc"}
                      onClick={() => handleSort("name")}
                      sx={{
                        fontFamily: "Outfit",
                        fontWeight: 600,
                        color: "#374151",
                        "&.Mui-active": {
                          color: "#2B3674",
                        },
                        "&:hover": {
                          color: "#2B3674",
                        }
                      }}
                    >
                      Offer Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === "expiryDate"}
                      direction={sortBy === "expiryDate" ? sortOrder : "asc"}
                      onClick={() => handleSort("expiryDate")}
                      sx={{
                        fontFamily: "Outfit",
                        fontWeight: 600,
                        color: "#374151",
                        "&.Mui-active": {
                          color: "#2B3674",
                        },
                        "&:hover": {
                          color: "#2B3674",
                        }
                      }}
                    >
                      Expiry Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Outfit", fontWeight: 600, color: "#374151" }}>Countdown</TableCell>
                  <TableCell sx={{ fontFamily: "Outfit", fontWeight: 600, color: "#374151" }}>Total Seats</TableCell>
                  <TableCell sx={{ fontFamily: "Outfit", fontWeight: 600, color: "#374151" }}>Remaining Seats</TableCell>
                  <TableCell sx={{ fontFamily: "Outfit", fontWeight: 600, color: "#374151" }}>Discount %</TableCell>
                  <TableCell sx={{ fontFamily: "Outfit", fontWeight: 600, color: "#374151" }}>Status</TableCell>
                  <TableCell sx={{ fontFamily: "Outfit", fontWeight: 600, color: "#374151" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {offers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: "center", py: 8 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: "50%",
                            backgroundColor: "#f8fafc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "2px solid #e5e7eb",
                          }}
                        >
                          <AddIcon sx={{ fontSize: 40, color: "#9ca3af" }} />
                        </Box>
                        <Box sx={{ textAlign: "center" }}>
                          <Typography
                            variant="h6"
                            sx={{
                              fontFamily: "Outfit",
                              fontWeight: 600,
                              color: "#374151",
                              mb: 1,
                            }}
                          >
                            No offers found
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "Outfit",
                              color: "#6b7280",
                              mb: 3,
                              maxWidth: 400,
                            }}
                          >
                            Get started by creating your first promotional offer to attract customers and boost sales.
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleCreateOffer}
                            sx={{
                              bgcolor: "#9048FB",
                              "&:hover": { bgcolor: "#7c3aed" },
                              textTransform: "none",
                              fontWeight: 600,
                              borderRadius: "8px",
                              px: 4,
                              py: 1.5,
                              fontFamily: "Outfit",
                              fontSize: "14px",
                            }}
                          >
                            Create Your First Offer
                          </Button>
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  offers.map((offer, index) => (
                    <TableRow 
                      key={offer._id}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#f8fafc",
                        },
                        "& .MuiTableCell-root": {
                          borderBottom: "1px solid #f1f5f9",
                          fontFamily: "Outfit",
                          fontSize: "14px",
                          color: "#374151",
                          py: 2,
                        }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500, color: "#2B3674" }}>{offer.name}</TableCell>
                      <TableCell>{formatDate(offer.expiryDate)}</TableCell>
                      <TableCell>
                        <CountdownTimer expiryDate={offer.expiryDate} />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#9048FB" }}>{offer.totalSeats}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#9048FB" }}>{offer.remainingSeats}</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#05CD99" }}>{offer.discountPercentage}%</TableCell>
                      <TableCell>
                        <Chip
                          label={offer.isActive ? "Active" : "Inactive"}
                          sx={{
                            backgroundColor: offer.isActive ? "#dcfce7" : "#f3f4f6",
                            color: offer.isActive ? "#166534" : "#6b7280",
                            fontWeight: 600,
                            fontSize: "12px",
                            fontFamily: "Outfit",
                            borderRadius: "6px",
                            height: "24px",
                          }}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleEditOffer(offer)}
                            sx={{
                              color: "#9048FB",
                              "&:hover": {
                                backgroundColor: "#f3e8ff",
                                color: "#7c3aed",
                              },
                              borderRadius: "6px",
                              p: 0.5,
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {offer.isActive ? (
                            <IconButton
                              size="small"
                              onClick={() => handleDeactivateOffer(offer._id)}
                              sx={{
                                color: "#f59e0b",
                                "&:hover": {
                                  backgroundColor: "#fef3c7",
                                  color: "#d97706",
                                },
                                borderRadius: "6px",
                                p: 0.5,
                              }}
                            >
                              <DeactivateIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => handleActivateOffer(offer._id)}
                              sx={{
                                color: "#10b981",
                                "&:hover": {
                                  backgroundColor: "#d1fae5",
                                  color: "#059669",
                                },
                                borderRadius: "6px",
                                p: 0.5,
                              }}
                            >
                              <ActivateIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteOffer(offer._id)}
                            disabled={offer.isActive}
                            sx={{
                              color: offer.isActive ? "#d1d5db" : "#ef4444",
                              "&:hover": {
                                backgroundColor: offer.isActive ? "transparent" : "#fee2e2",
                                color: offer.isActive ? "#d1d5db" : "#dc2626",
                              },
                              borderRadius: "6px",
                              p: 0.5,
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              </Table>
            </TableContainer>
            
            {offers.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={totalOffers}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: "1px solid #f1f5f9",
                  mt: 2,
                  "& .MuiTablePagination-toolbar": {
                    fontFamily: "Outfit",
                    color: "#374151",
                  },
                  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                    fontFamily: "Outfit",
                    fontSize: "14px",
                    color: "#6b7280",
                  },
                  "& .MuiTablePagination-select": {
                    fontFamily: "Outfit",
                    fontSize: "14px",
                    color: "#374151",
                  }
                }}
              />
            )}
          </Box>
      </Box>

      {/* Create/Edit Offer Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            fontFamily: "Outfit",
          }
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Outfit",
            fontSize: "18px",
            fontWeight: 700,
            color: "#2B3674",
            borderBottom: "1px solid #f1f5f9",
            pb: 2,
            position: "relative",
          }}
        >
          {editingOffer ? "Edit Offer" : "Create New Offer"}
          <IconButton
            onClick={() => setOpenDialog(false)}
            sx={{ 
              position: "absolute", 
              right: 16, 
              top: 16,
              color: "#6b7280",
              "&:hover": {
                backgroundColor: "#f3f4f6",
                color: "#374151",
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Offer Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontFamily: "Outfit",
                    "& fieldset": {
                      borderColor: "#e5e7eb",
                    },
                    "&:hover fieldset": {
                      borderColor: "#9048FB",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#9048FB",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontFamily: "Outfit",
                    color: "#6b7280",
                    "&.Mui-focused": {
                      color: "#9048FB",
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="datetime-local"
                value={formData.expiryDate}
                onChange={(e) => setFormData({ ...formData, expiryDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontFamily: "Outfit",
                    "& fieldset": {
                      borderColor: "#e5e7eb",
                    },
                    "&:hover fieldset": {
                      borderColor: "#9048FB",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#9048FB",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontFamily: "Outfit",
                    color: "#6b7280",
                    "&.Mui-focused": {
                      color: "#9048FB",
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Total Seats"
                type="number"
                value={formData.totalSeats}
                onChange={(e) => setFormData({ ...formData, totalSeats: e.target.value })}
                required
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontFamily: "Outfit",
                    "& fieldset": {
                      borderColor: "#e5e7eb",
                    },
                    "&:hover fieldset": {
                      borderColor: "#9048FB",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#9048FB",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontFamily: "Outfit",
                    color: "#6b7280",
                    "&.Mui-focused": {
                      color: "#9048FB",
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Remaining Seats"
                type="number"
                value={formData.remainingSeats}
                onChange={(e) => setFormData({ ...formData, remainingSeats: e.target.value })}
                required
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontFamily: "Outfit",
                    "& fieldset": {
                      borderColor: "#e5e7eb",
                    },
                    "&:hover fieldset": {
                      borderColor: "#9048FB",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#9048FB",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontFamily: "Outfit",
                    color: "#6b7280",
                    "&.Mui-focused": {
                      color: "#9048FB",
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Discount Percentage"
                type="number"
                value={formData.discountPercentage}
                onChange={(e) => setFormData({ ...formData, discountPercentage: e.target.value })}
                required
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontFamily: "Outfit",
                    "& fieldset": {
                      borderColor: "#e5e7eb",
                    },
                    "&:hover fieldset": {
                      borderColor: "#9048FB",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#9048FB",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    fontFamily: "Outfit",
                    color: "#6b7280",
                    "&.Mui-focused": {
                      color: "#9048FB",
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#9048FB",
                        "& + .MuiSwitch-track": {
                          backgroundColor: "#9048FB",
                        },
                      },
                    }}
                  />
                }
                label="Activate this offer"
                sx={{
                  fontFamily: "Outfit",
                  color: "#374151",
                  fontWeight: 500,
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: "1px solid #f1f5f9", gap: 1 }}>
          <Button 
            onClick={() => setOpenDialog(false)}
            sx={{
              fontFamily: "Outfit",
              fontWeight: 500,
              color: "#6b7280",
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1,
              "&:hover": {
                backgroundColor: "#f3f4f6",
                color: "#374151",
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveOffer} 
            variant="contained"
            sx={{
              fontFamily: "Outfit",
              fontWeight: 600,
              backgroundColor: "#9048FB",
              "&:hover": {
                backgroundColor: "#7c3aed",
              },
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1,
            }}
          >
            {editingOffer ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiSnackbarContent-root": {
            borderRadius: "12px",
            fontFamily: "Outfit",
            fontWeight: 500,
          }
        }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{
            borderRadius: "12px",
            fontFamily: "Outfit",
            fontWeight: 500,
            "& .MuiAlert-message": {
              fontFamily: "Outfit",
            },
            "& .MuiAlert-icon": {
              fontSize: "20px",
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog for Active Offer */}
      <Dialog
        open={confirmationDialog.open}
        onClose={handleCancelActivation}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            fontFamily: "Outfit",
          }
        }}
      >
        <DialogTitle sx={{ 
          fontFamily: "Outfit", 
          fontWeight: 600, 
          fontSize: "18px",
          color: "#374151"
        }}>
          {confirmationDialog.title}
        </DialogTitle>
        <DialogContent sx={{ fontFamily: "Outfit" }}>
          <Typography sx={{ 
            fontFamily: "Outfit", 
            fontSize: "14px",
            color: "#6B7280",
            lineHeight: 1.5
          }}>
            {confirmationDialog.message}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={handleCancelActivation}
            variant="outlined"
            sx={{
              fontFamily: "Outfit",
              fontWeight: 500,
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1,
              borderColor: "#D1D5DB",
              color: "#374151",
              "&:hover": {
                borderColor: "#9CA3AF",
                backgroundColor: "#F9FAFB",
              },
            }}
          >
            No, Keep Current
          </Button>
          <Button
            onClick={handleConfirmActivation}
            variant="contained"
            sx={{
              fontFamily: "Outfit",
              fontWeight: 600,
              backgroundColor: "#9048FB",
              "&:hover": {
                backgroundColor: "#7c3aed",
              },
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              py: 1,
            }}
          >
            Yes, Activate New
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default OffersManagement;
